import { MemCommonEditorStore } from "@/components/note/store/mem-common-editor-store";
import { isMac } from "@/domains/platform/isMac";
import {
  MDSIconKind,
  MDSIconButton,
  MDSPopover,
  MDSText,
  MDSTextField,
  MDSTextFieldVariant,
  MDSTextColor,
  MDSTextVariant,
  MDSIconButtonSize,
} from "@/mds";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useCallback, useRef } from "react";

interface FindInNoteModalProps {
  store?: MemCommonEditorStore;
}

export const FindInNoteModal = observer(({ store }: FindInNoteModalProps) => {
  const isOpen = !!store?.findInNote?.isOpen;
  const close = store?.findInNote?.close;

  const inputRef = useRef<HTMLInputElement>(null);

  const handleOnKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      switch (event.key) {
        case "ArrowUp": {
          event.preventDefault();
          store?.findInNote?.findPrevious();
          return true;
        }

        case "g":
          if (isMac() ? !event.metaKey : !event.ctrlKey) return;
        // Fall through

        case "ArrowDown":
        case "Enter":
        case "Return": {
          event.preventDefault();
          store?.findInNote?.findNext();
          return true;
        }

        case "Escape": {
          event.preventDefault();
          close?.();
          return true;
        }
      }
    },
    [close, store]
  );

  return (
    <StyledPopover
      isNonModal
      styling={false}
      triggerRef={store?.findInNote?.anchorRef}
      isOpen={!!isOpen}
      onOpenChange={value => {
        if (!value) close?.();
      }}
      shouldCloseOnInteractOutside={() => false}
      placement="bottom end"
    >
      <StyledOverlay $isOpen={isOpen}>
        <Expanding>
          <FindInput
            autoFocus
            ref={inputRef}
            variant={MDSTextFieldVariant.Form}
            placeholder="Find in note"
            value={store?.findInNote?.searchQuery}
            onChange={store?.findInNote?.setSearchQuery}
            onKeyDown={handleOnKeyDown}
          />
        </Expanding>
        {!!store?.findInNote?.searchQuery && (
          <>
            <MatchCounterText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextS}>
              {store.findInNote.matchesCount
                ? `${store.findInNote.currentMatchIndex + 1} of ${store.findInNote.matchesCount}`
                : "Not found"}
            </MatchCounterText>
            {!!store.findInNote.matchesCount && (
              <>
                <MDSIconButton
                  size={MDSIconButtonSize.Small}
                  iconKind={MDSIconKind.AngleUp}
                  onClick={store?.findInNote?.findPrevious}
                />
                <MDSIconButton
                  size={MDSIconButtonSize.Small}
                  iconKind={MDSIconKind.AngleDown}
                  onClick={store?.findInNote?.findNext}
                />
              </>
            )}
          </>
        )}
        <MDSIconButton size={MDSIconButtonSize.Small} iconKind={MDSIconKind.Exit} onClick={close} />
      </StyledOverlay>
    </StyledPopover>
  );
});

const StyledPopover = styled(MDSPopover)`
  position: relative;
`;

const StyledOverlay = styled.div<{ $isOpen: boolean }>`
  background-color: ${({ theme }) => theme.colors.surface.modal};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  padding: ${({ theme }) => `0 ${theme.spacing[3]} 0 ${theme.spacing[5]}`};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  width: 336px;
  max-width: 90vw;
  height: 40px;
  overflow: hidden; /* clip inner scrollbar to rounded corners */
  ${({ $isOpen }) => ($isOpen ? `display: flex;` : `display: none;`)}
  align-items: center;
`;

const Expanding = styled.div`
  flex: 1;
`;

const FindInput = styled(MDSTextField)`
  border: unset;
  background-color: transparent;
  padding: 0;
`;

const MatchCounterText = styled(MDSText)`
  margin: auto 0 auto ${({ theme }) => theme.spacing[1]};
  white-space: nowrap;
`;
